import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Loader2, MapPin, Navigation, Camera, User } from 'lucide-react';
import { PROFILE } from '@/constants/testIds';
import { toast } from 'sonner';
import axios from 'axios';

const Profile = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    city: '',
    lat: '',
    lng: '',
    favorite_genres: [],
    reading_preferences: '',
    dob: ''
  });

  const genreOptions = [
    'Romantasy', 'Smut', 'Romance', 'Fantasy', 'Sci-Fi',
    'Mystery', 'Thriller', 'Horror', 'Historical', 'Adventure',
    'Fiction', 'Non-Fiction', 'Biography', 'Self-Help', 'Poetry',
    'Drama', 'Comedy', 'Young Adult', 'Contemporary', 'Classics'
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        bio: user.bio || '',
        city: user.location?.city || '',
        lat: user.location?.lat || '',
        lng: user.location?.lng || '',
        favorite_genres: user.favorite_genres || [],
        reading_preferences: user.reading_preferences || '',
        dob: user.dob || ''
      });
    }
  }, [user]);

  // Client-side 18+ bounds for date picker
  const today = new Date();
  const maxDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];
  const minDob = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      favorite_genres: prev.favorite_genres.includes(genre)
        ? prev.favorite_genres.filter(g => g !== genre)
        : [...prev.favorite_genres, genre]
    }));
  };

  const uploadPicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploadingPic(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/picture`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Profile picture updated!');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPic(false);
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/geocode/reverse`,
            { params: { lat: latitude, lng: longitude }, withCredentials: true }
          );
          setFormData(prev => ({
            ...prev,
            city: data.city || 'Unknown',
            lat: latitude.toFixed(6),
            lng: longitude.toFixed(6)
          }));
          toast.success(`Location detected: ${data.city}`);
        } catch (err) {
          setFormData(prev => ({
            ...prev,
            lat: latitude.toFixed(6),
            lng: longitude.toFixed(6)
          }));
          toast.success('Coordinates detected');
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        setDetectingLocation(false);
        toast.error('Unable to retrieve your location. Please allow location access.');
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        bio: formData.bio,
        location: formData.city ? {
          city: formData.city,
          lat: parseFloat(formData.lat) || 0,
          lng: parseFloat(formData.lng) || 0
        } : null,
        favorite_genres: formData.favorite_genres,
        reading_preferences: formData.reading_preferences
      };
      // Only send DOB when the user hasn't set one yet (legacy users). Once set, it's locked.
      if (!user?.dob && formData.dob) {
        payload.dob = formData.dob;
      }
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile`,
        payload,
        { withCredentials: true }
      );
      toast.success('Profile updated successfully!');
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-[#5C5955] hover:text-[#6B7A4F]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="glass-card p-8 rounded-2xl">
          <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-2">
            Your <span className="font-bold">Profile</span>
          </h1>
          <p className="text-[#5C5955] mb-8">
            Tell us about your reading preferences to find your perfect bookish bestie
          </p>

          <form data-testid={PROFILE.form} onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24 rounded-full overflow-hidden bg-[#E2E6D5] flex items-center justify-center flex-shrink-0">
                {user?.profile_picture ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${user.profile_picture}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-[#6B7A4F]" strokeWidth={1.5} />
                )}
              </div>
              <div>
                <Label htmlFor="pic-upload" className="cursor-pointer">
                  <div
                    data-testid="profile-picture-upload-btn"
                    className="inline-flex items-center gap-2 border-2 border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                  >
                    {uploadingPic ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4" />{user?.profile_picture ? 'Change Photo' : 'Upload Photo'}</>
                    )}
                  </div>
                </Label>
                <input
                  id="pic-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={uploadPicture}
                  className="hidden"
                  disabled={uploadingPic}
                  data-testid="profile-picture-input"
                />
                <p className="text-xs text-[#5C5955] mt-2">JPG, PNG, WebP · Max 5MB</p>
              </div>
            </div>

            {/* Date of Birth — locked once set. Required for matching. */}
            <div className="space-y-2">
              <Label htmlFor="dob" className="text-[#2D2A26] font-medium">
                Date of Birth {!user?.dob && <span className="text-[#6B7A4F]">*</span>}
              </Label>
              {user?.dob ? (
                <div
                  data-testid="profile-dob-locked"
                  className="flex items-center justify-between bg-[#E2E6D5]/40 border border-[#E1D9CD] rounded-xl px-4 py-3"
                >
                  <span className="text-[#2D2A26]">
                    {new Date(user.dob).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    {user.age != null && <span className="text-[#5C5955] ml-2">· Age {user.age}</span>}
                  </span>
                  <span className="text-xs text-[#5C5955]">Locked</span>
                </div>
              ) : (
                <>
                  <Input
                    id="dob"
                    data-testid="profile-dob-input"
                    type="date"
                    value={formData.dob}
                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                    max={maxDob}
                    min={minDob}
                    required
                    className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
                  />
                  <p className="text-xs text-[#5C5955]">
                    You must be <strong>18 or older</strong>. Used to match you with readers within an 8-year age range. This can only be set once.
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio" className="text-[#2D2A26] font-medium">
                Bio
              </Label>
              <Textarea
                id="bio"
                data-testid={PROFILE.bioInput}
                placeholder="Tell us about yourself and your reading journey..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={4}
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[#2D2A26] font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </Label>
                <Button
                  type="button"
                  onClick={detectLocation}
                  disabled={detectingLocation}
                  variant="outline"
                  size="sm"
                  className="border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white rounded-full text-xs"
                  data-testid="profile-detect-location-btn"
                >
                  {detectingLocation ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Detecting...
                    </>
                  ) : (
                    <>
                      <Navigation className="w-3 h-3 mr-1" />
                      Detect My Location
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  data-testid={PROFILE.locationInput}
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-[#2D2A26] font-medium">
                Favorite Genres (Select all that apply)
              </Label>
              <div data-testid={PROFILE.genresSelect} className="flex flex-wrap gap-2">
                {genreOptions.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className={`genre-badge ${
                      formData.favorite_genres.includes(genre)
                        ? 'bg-[#6B7A4F] text-white'
                        : 'bg-[#E2E6D5] text-[#6B7A4F]'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferences" className="text-[#2D2A26] font-medium">
                Reading Preferences
              </Label>
              <Textarea
                id="preferences"
                data-testid={PROFILE.preferencesInput}
                placeholder="Do you prefer physical books or e-books? Morning or evening reading? Coffee shop or cozy home?"
                value={formData.reading_preferences}
                onChange={(e) => setFormData({ ...formData, reading_preferences: e.target.value })}
                rows={3}
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <Button
              data-testid={PROFILE.saveBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full text-base font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Profile'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
